// ═══════════════════════════════════════════════════════════════════════════════
// Password input with show/hide toggle (accessibility: button + aria-pressed)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';

type PasswordFieldProps = {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  autoComplete?: string;
  disabled?: boolean;
  required?: boolean;
  showLabels: { show: string; hide: string };
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
};

export const PasswordField = ({
  id,
  name,
  label,
  value,
  onChange,
  autoComplete = 'current-password',
  disabled = false,
  required = true,
  showLabels,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: PasswordFieldProps) => {
  const [visible, setVisible] = useState(false);

  const handleToggle = useCallback(() => {
    setVisible((v) => !v);
  }, []);

  const handleToggleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  }, [handleToggle]);

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="relative mt-1">
        <input
          id={id}
          name={name}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          required={required}
          disabled={disabled}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-gray-300 py-2 pr-12 pl-3 text-gray-900 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none disabled:bg-gray-50"
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
        />
        <button
          type="button"
          tabIndex={0}
          aria-pressed={visible}
          aria-label={visible ? showLabels.hide : showLabels.show}
          disabled={disabled}
          onClick={handleToggle}
          onKeyDown={handleToggleKeyDown}
          className="absolute top-1/2 right-2 -translate-y-1/2 rounded px-2 py-1 text-xs font-medium text-primary-700 underline focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
        >
          {visible ? showLabels.hide : showLabels.show}
        </button>
      </div>
    </div>
  );
};
